'use client'

import { createRef, ReactNode, useEffect } from 'react'

interface ConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    title?: string
    message?: string
    loading?: boolean
    confirmButtonText?: string
    cancelButtonText?: string
    confirmButtonClass?: string
    /** Conteúdo extra abaixo da mensagem (ex.: um campo que a confirmação precisa). */
    children?: ReactNode
}

export default function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirmar ação',
    message = 'Tem certeza de que deseja continuar?',
    loading = false,
    confirmButtonText = 'Confirmar',
    cancelButtonText = 'Cancelar',
    confirmButtonClass = 'btn btn-error !border-0 !rounded-lg',
    children,
}: ConfirmModalProps) {
    const modalRef = createRef<HTMLDialogElement>()

    if (confirmButtonClass && confirmButtonClass.split(' ').length === 1) {
        confirmButtonClass = `btn ${confirmButtonClass} !border-0 !rounded-lg`
    }

    useEffect(() => {
        if (isOpen) {
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
        }
    }, [isOpen])

    const handleConfirm = () => {
        onConfirm()
    }

    const handleClose = () => {
        onClose()
    }

    return (
        // Esc fecha o <dialog> nativo sem passar pelo `onClose`: a tela continuava com
        // `isOpen` verdadeiro e o próximo clique para abrir não mudava nada. Aqui o Esc
        // vira o mesmo "Cancelar" (e é ignorado enquanto a ação está em andamento).
        <dialog
            className="modal z-50"
            ref={modalRef}
            onCancel={e => {
                e.preventDefault()
                if (!loading) handleClose()
            }}
        >
            <div className="modal-box !max-w-md !mx-4 !p-0">
                <div
                    className="flex items-center justify-between p-4 border-b
                 border-gray-100 modal-header"
                >
                    <h2 className="text-lg font-bold text-gray-900 modal-header-title font-nunito">
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={loading}
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="p-6">
                    <div className="">
                        <p className="text-base-1">{message}</p>
                    </div>
                    {children && <div className="mt-5">{children}</div>}
                </div>

                {/*
                    `type="button"` é obrigatório: sem ele o padrão do HTML é "submit",
                    e quando este modal é renderizado dentro de um <form> (acontece com
                    formulários aninhados, ex.: o modal de variação do catálogo) clicar
                    em Confirmar/Cancelar submete o form ancestral.
                */}
                <div className="flex justify-end gap-3 p-6 ">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="btn btn-outline-light !rounded-xl"
                    >
                        {cancelButtonText}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={loading}
                        className={confirmButtonClass}
                    >
                        {loading ? 'Processando...' : confirmButtonText}
                    </button>
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={handleClose}>close</button>
            </form>
        </dialog>
    )
}
